import React from 'react';
import {
  Box, Tabs, Tab, Select, MenuItem, FormControl,
  Typography, useMediaQuery, useTheme, alpha,
} from '@mui/material';
import { ProChip } from '@/components/common/ProChip';
import { useSearchParams } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface AdaptiveTabItem {
  value: string;
  label: string;
  icon?: React.ReactElement;
  iconPosition?: 'start' | 'end' | 'top' | 'bottom';
  iDoc?: string;
  chip?: string;
  /** Таб видимий, але неклікабельний (напр. тимчасове обмеження за роллю) — не плутати з
   * видаленням табу зі списку (це ховає його зовсім). */
  disabled?: boolean;
}

function readStoredTab(storageKey?: string): string | null {
  if (!storageKey) return null;
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/**
 * Active tab in URL mode: `?<searchParamKey>=` → last selected tab (`storageKey`) → `defaultValue`; disabled tabs are
 * skipped. Pages render content by this value so that it always matches the highlighted tab.
 */
export function useAdaptiveTab(
  tabs: AdaptiveTabItem[],
  options: { searchParamKey: string; defaultValue: string; storageKey?: string },
): string {
  const [searchParams] = useSearchParams();
  const isSelectable = (value: string | null): value is string =>
    !!value && tabs.some((t) => t.value === value && !t.disabled);
  const param = searchParams.get(options.searchParamKey);
  if (isSelectable(param)) return param;
  const stored = readStoredTab(options.storageKey);
  return isSelectable(stored) ? stored : options.defaultValue;
}

type AdaptiveTabsProps = {
  tabs: AdaptiveTabItem[];
  /** Кількість табів, при перевищенні якої на мобільному показується Select. Default: 2 */
  mobileThreshold?: number;
} & (
  | { searchParamKey: string; defaultValue: string; storageKey?: string; value?: never; onChange?: never }
  | { value: string; onChange: (value: string) => void; searchParamKey?: never; defaultValue?: never; storageKey?: never }
);

export const AdaptiveTabs: React.FC<AdaptiveTabsProps> = ({
  tabs,
  mobileThreshold = 2,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [, setSearchParams] = useSearchParams();
  const urlTab = useAdaptiveTab(tabs, {
    searchParamKey: rest.searchParamKey ?? '',
    defaultValue: rest.defaultValue ?? '',
    storageKey: rest.storageKey,
  });

  let value: string;
  let handleChange: (v: string) => void;

  if ('searchParamKey' in rest && rest.searchParamKey) {
    value = urlTab;
    handleChange = (v: string) => {
      if (rest.storageKey) {
        try {
          localStorage.setItem(rest.storageKey, v);
        } catch {
          // Storage unavailable: the URL still keeps the tab.
        }
      }
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set(rest.searchParamKey!, v);
        return next;
      });
    };
  } else {
    value = rest.value!;
    handleChange = rest.onChange!;
  }

  const showSelect = isMobile && tabs.length > mobileThreshold;

  if (showSelect) {
    const activeTab = tabs.find(t => t.value === value);
    const primaryColor = theme.palette.primary.main;

    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
        <FormControl fullWidth size="small">
          <Select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
            renderValue={() => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                {activeTab?.icon && React.cloneElement(activeTab.icon, { fontSize: 'small' })}
                <Typography variant="body2" fontWeight={600} color="primary" component="span">
                  {activeTab?.label}
                </Typography>
              </Box>
            )}
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(primaryColor, 0.06),
              color: primaryColor,
              fontWeight: 600,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(primaryColor, 0.3),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(primaryColor, 0.6),
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: primaryColor,
              },
              '& .MuiSelect-icon': {
                color: primaryColor,
              },
            }}
          >
            {tabs.map(tab => (
              <MenuItem
                key={tab.value}
                value={tab.value}
                disabled={tab.disabled}
                sx={{
                  fontWeight: tab.value === value ? 600 : 400,
                  color: tab.value === value ? 'primary.main' : 'text.primary',
                  gap: 1,
                }}
              >
                {tab.icon && React.cloneElement(tab.icon, { fontSize: 'small' })}
                {tab.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        value={value}
        onChange={(_, v) => handleChange(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              tab.chip ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {tab.label}
                  <ProChip />
                </Box>
              ) : tab.label
            }
            icon={tab.icon}
            iconPosition={tab.icon ? (tab.iconPosition ?? 'start') : undefined}
            sx={tab.icon ? { minHeight: 48 } : undefined}
            disabled={tab.disabled}
            {...(tab.iDoc ? { 'data-i-doc': tab.iDoc } : {})}
          />
        ))}
      </Tabs>
    </Box>
  );
};
